import {
    CheckCircleFilled,
    CloseCircleFilled,
    DownloadOutlined,
    EyeOutlined,
    LoadingOutlined,
    ReconciliationOutlined,
    RocketOutlined,
    SyncOutlined,
    ThunderboltOutlined,
    WarningOutlined,
} from "@ant-design/icons";
import {
    Alert, Button, Card, Col, Progress, Row, Space, Statistic, Steps, Table, Typography,
} from "antd";
import * as XLSX from "xlsx";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import PageLayout from "../../components/PageLayout";
import axiosInstance from "../../variables/api/axiosInstance";

const { Title, Text } = Typography;
const POLL_INTERVAL_MS = 2500;
const ETAT_LABELS = { EN_COURS: "En cours", TERMINE: "Terminé", ERREUR: "Erreur", INCONNU: "Inconnu" };
const fmt = (n) => (n == null ? "0" : Math.round(n).toLocaleString("fr-FR")) + " FCFA";

export default function RepriseGumVtEvalCourante() {
    const [step, setStep]         = useState(0);   // 0 lancement, 1 aperçu, 2 application, 3 rapport
    const [apercu, setApercu]     = useState(null);
    const [rapport, setRapport]   = useState(null);
    const [loading, setLoading]   = useState(false);
    const [progress, setProgress] = useState(0);
    const [jobId, setJobId]       = useState(null);
    const [jobEtat, setJobEtat]   = useState(null);
    const pollRef = useRef(null);

    useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

    const previsualiser = async () => {
        setLoading(true); setApercu(null);
        try {
            const res = await axiosInstance.post(
                "/api/migration/reprise-gumvt/corriger-evaluation-courante/preview", {});
            setApercu(res.data); setStep(1);
        } catch (err) {
            toast.error("Erreur aperçu : " + (err.response?.data?.message || err.message));
        } finally { setLoading(false); }
    };

    const appliquer = async () => {
        setLoading(true); setRapport(null); setProgress(5); setJobEtat("EN_COURS"); setStep(2);
        try {
            const res = await axiosInstance.post(
                "/api/migration/reprise-gumvt/corriger-evaluation-courante", {});
            const id = res.data?.jobId;
            if (!id) throw new Error("Aucun jobId reçu du serveur.");
            setJobId(id); demarrerPolling(id);
        } catch (err) {
            setLoading(false); setJobEtat("ERREUR");
            toast.error("Erreur : " + (err.response?.data?.message || err.message));
        }
    };

    const demarrerPolling = useCallback((id) => {
        let ticks = 0;
        pollRef.current = setInterval(async () => {
            ticks++;
            setProgress(p => Math.min(p + Math.max(1, 5 - Math.floor(ticks / 5)), 90));
            try {
                const res = await axiosInstance.get(`/api/migration/job/${id}`);
                const statut = res.data;
                if (statut.etat === "TERMINE" || statut.etat === "ERREUR") {
                    clearInterval(pollRef.current);
                    setProgress(100); setJobEtat(statut.etat); setLoading(false);
                    if (statut.etat === "TERMINE") {
                        setRapport(statut.resultat); setStep(3);
                        toast.success("Recalage évaluation / SAP terminé !");
                    } else {
                        toast.error("Erreur : " + (statut.erreur || "Erreur inconnue"));
                    }
                }
            } catch (err) {
                clearInterval(pollRef.current); setLoading(false); setJobEtat("ERREUR");
                toast.error("Erreur polling : " + err.message);
            }
        }, POLL_INTERVAL_MS);
    }, []);

    const exportRapport = (d) => {
        if (!d) return;
        const wb = XLSX.utils.book_new();
        const resume = [
            ["Indicateur", "Valeur"],
            ["Sinistres compagnies examinés", d.totalSinistres],
            ["À corriger", d.aCorriger],
            ["Déjà cohérents", d.dejaBons],
            ["Écart total d'évaluation", Math.round(d.ecartTotal || 0)],
        ];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(resume), "Résumé");
        const ech = [
            ["N° sinistre", "Éval. avant", "Éval. après", "SAP avant", "SAP après", "Encaissé"],
            ...(d.echantillon || []).map(e => [e.numSinistre, Math.round(e.evaluationAvant),
                Math.round(e.evaluationApres), Math.round(e.sapAvant), Math.round(e.sapApres),
                Math.round(e.encaisse)]),
        ];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(ech), "Échantillon");
        XLSX.writeFile(wb, `correction_evaluation_sap_compagnies_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    const reinitialiser = () => {
        if (pollRef.current) clearInterval(pollRef.current);
        setStep(0); setApercu(null); setRapport(null);
        setLoading(false); setProgress(0); setJobId(null); setJobEtat(null);
    };

    const colsEch = [
        { title: "N° sinistre", dataIndex: "numSinistre", key: "numSinistre", width: 150,
          render: v => <Text code style={{ fontSize: 11 }}>{v}</Text> },
        { title: "Éval. avant", dataIndex: "evaluationAvant", key: "evaluationAvant", align: "right",
          render: v => <Text type="danger">{fmt(v)}</Text> },
        { title: "Éval. après", dataIndex: "evaluationApres", key: "evaluationApres", align: "right",
          render: v => <Text type="success">{fmt(v)}</Text> },
        { title: "SAP avant", dataIndex: "sapAvant", key: "sapAvant", align: "right", render: fmt },
        { title: "SAP après", dataIndex: "sapApres", key: "sapApres", align: "right",
          render: v => <Text strong>{fmt(v)}</Text> },
        { title: "Encaissé", dataIndex: "encaisse", key: "encaisse", align: "right", render: fmt },
    ];

    const Indics = ({ d }) => (
        <Row gutter={[16, 16]}>
            <Col xs={12} sm={8} md={6}>
                <Statistic title="Sinistres compagnies examinés" value={d.totalSinistres}
                    valueStyle={{ fontSize: 18, color: "#242166" }} />
            </Col>
            <Col xs={12} sm={8} md={6}>
                <Statistic title="À corriger" value={d.aCorriger}
                    valueStyle={{ color: "#52c41a", fontSize: 18 }} prefix={<CheckCircleFilled />} />
            </Col>
            <Col xs={12} sm={8} md={6}>
                <Statistic title="Déjà cohérents" value={d.dejaBons}
                    valueStyle={{ fontSize: 18, color: "#8c8c8c" }} />
            </Col>
            <Col xs={12} sm={8} md={6}>
                <Statistic title="Écart total d'éval." value={Math.round(d.ecartTotal || 0)} suffix="F"
                    valueStyle={{ fontSize: 16, color: "#242166" }} />
            </Col>
        </Row>
    );

    return (
        <PageLayout currentMenu="REPRISE">
            <div style={{ padding: "24px 0" }}>
                <Space align="center" style={{ marginBottom: 24 }}>
                    <ReconciliationOutlined style={{ fontSize: 28, color: "#242166" }} />
                    <div>
                        <Title level={4} style={{ margin: 0, color: "#242166" }}>
                            Recaler évaluation & SAP — Compagnies
                        </Title>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Sans fichier — hors Pool GUM/VT · évaluation = dernière évaluation · SAP = éval − encaissé (≥ 0)
                        </Text>
                    </div>
                </Space>

                <Steps current={step} style={{ marginBottom: 32 }} items={[
                    { title: "Lancement" }, { title: "Aperçu" }, { title: "Application" }, { title: "Rapport" },
                ]} />

                {step === 0 && (
                    <Card>
                        <Alert type="info" showIcon style={{ marginBottom: 16 }}
                            message="Aucun fichier requis. Pour chaque sinistre de COMPAGNIE (tout sauf le Pool GUM/VT) : évaluation courante = dernière évaluation enregistrée (= ouverture s'il n'y a pas eu de réévaluation) ; SAP = max(0, évaluation − règlements encaissés). Le SAP n'est jamais négatif. Les dossiers CLOS (terminé, sans suite) sont exclus — leur SAP/évaluation à 0 est correct. Seuls les dossiers incohérents sont corrigés. L'aperçu ne modifie rien." />
                        <div style={{ textAlign: "right" }}>
                            <Button type="primary" size="large" icon={<EyeOutlined />} loading={loading}
                                onClick={previsualiser} style={{ background: "#242166" }}>
                                Prévisualiser
                            </Button>
                        </div>
                    </Card>
                )}

                {step === 1 && apercu && (
                    <div>
                        <Alert type="warning" showIcon style={{ marginBottom: 20 }}
                            message="Aperçu — aucune écriture effectuée"
                            description={`${apercu.aCorriger} sinistre(s) compagnie à corriger sur ${apercu.totalSinistres} examinés. Vérifiez l'échantillon puis appliquez.`} />
                        <Card style={{ marginBottom: 20 }}><Indics d={apercu} /></Card>
                        {apercu.echantillon?.length > 0 && (
                            <Card size="small" style={{ marginBottom: 20 }}
                                title={<Text strong>Échantillon des corrections ({apercu.echantillon.length})</Text>}>
                                <Table dataSource={apercu.echantillon.map((e, i) => ({ key: i, ...e }))}
                                    columns={colsEch} size="small" pagination={{ pageSize: 10, size: "small" }}
                                    scroll={{ y: 340 }} />
                            </Card>
                        )}
                        {apercu.lignesErreur?.length > 0 && (
                            <Card size="small" style={{ marginBottom: 20 }}
                                title={<Space><WarningOutlined style={{ color: "#faad14" }} />
                                    <Text strong>Anomalies ({apercu.lignesErreur.length})</Text></Space>}>
                                <Table dataSource={apercu.lignesErreur.map((m, i) => ({ key: i, message: m }))}
                                    columns={[{ title: "Détail", dataIndex: "message", key: "message",
                                        render: v => <Text type="warning" style={{ fontSize: 12 }}>{v}</Text> }]}
                                    size="small" pagination={{ pageSize: 15 }} scroll={{ y: 220 }} />
                            </Card>
                        )}
                        <Space>
                            <Button onClick={reinitialiser}>Annuler</Button>
                            <Button icon={<DownloadOutlined />} onClick={() => exportRapport(apercu)}>Exporter l'aperçu</Button>
                            <Button type="primary" icon={<ThunderboltOutlined />} disabled={apercu.aCorriger === 0}
                                onClick={appliquer} style={{ background: "#242166" }}>
                                Appliquer en base ({apercu.aCorriger})
                            </Button>
                        </Space>
                    </div>
                )}

                {step === 2 && (
                    <Card>
                        <div style={{ textAlign: "center", padding: "40px 0" }}>
                            {loading ? (
                                <>
                                    <LoadingOutlined style={{ fontSize: 48, color: "#242166", marginBottom: 20 }} />
                                    <div style={{ marginBottom: 8 }}><Text strong style={{ fontSize: 16 }}>Recalage en cours…</Text></div>
                                    {jobId && (
                                        <div style={{ marginBottom: 12 }}>
                                            <Text type="secondary" style={{ fontSize: 11 }}>Job ID : <Text code>{jobId}</Text></Text>
                                        </div>
                                    )}
                                    <Progress percent={progress} strokeColor="#242166" style={{ maxWidth: 500, margin: "0 auto" }} />
                                </>
                            ) : jobEtat === "ERREUR" ? (
                                <>
                                    <CloseCircleFilled style={{ fontSize: 48, color: "#ff4d4f", marginBottom: 20 }} />
                                    <div><Text type="danger" strong style={{ fontSize: 16 }}>L'opération a échoué.</Text></div>
                                    <Button onClick={reinitialiser} style={{ marginTop: 16 }}>Recommencer</Button>
                                </>
                            ) : null}
                        </div>
                    </Card>
                )}

                {step === 3 && rapport && (
                    <div>
                        <Alert type="success" showIcon icon={<CheckCircleFilled style={{ color: "#52c41a" }} />}
                            message={`${rapport.aCorriger} sinistre(s) compagnie recalés (évaluation + SAP)`}
                            description={`Durée : ${(rapport.dureeMs / 1000).toFixed(1)}s — sauvegarde réversible dans m_sinistre_bak_evalcour`} style={{ marginBottom: 20 }} />
                        <Card style={{ marginBottom: 20 }}><Indics d={rapport} /></Card>
                        {rapport.echantillon?.length > 0 && (
                            <Card size="small" style={{ marginBottom: 20 }}
                                title={<Text strong>Échantillon des corrections ({rapport.echantillon.length})</Text>}>
                                <Table dataSource={rapport.echantillon.map((e, i) => ({ key: i, ...e }))}
                                    columns={colsEch} size="small" pagination={{ pageSize: 10, size: "small" }}
                                    scroll={{ y: 340 }} />
                            </Card>
                        )}
                        <Space>
                            <Button icon={<DownloadOutlined />} onClick={() => exportRapport(rapport)}>Exporter le rapport</Button>
                            <Button type="primary" icon={<RocketOutlined />} onClick={reinitialiser} style={{ background: "#242166" }}>
                                Nouveau recalage
                            </Button>
                        </Space>
                    </div>
                )}
            </div>
        </PageLayout>
    );
}
